import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import type { TzRules } from '@/lib/tzRules';

interface SetTimezoneResponse {
  /** Resolved IANA name after the upsert (e.g. "America/Los_Angeles"). */
  timezone: string;
  /** The id that got persisted (echoes the request input). */
  timezoneID: number;
  /** The server's offset rules, now covering the picked zone (SCRUM-2428). */
  tzRules?: TzRules;
}

interface ApiResponse<T> {
  data: T;
}

/**
 * Persist the user's preferred timezone to `users_timezones` via
 * `PUT /api/v5/user/timezone` and refresh the in-memory state so every
 * `useUserTimezone()` consumer re-renders without a page reload.
 *
 * On success:
 *   1. Patches `useAuthStore.user.{timezone,timezoneID}` so the picker
 *      stays in sync and `useUserTimezone()` returns the new IANA name.
 *   2. Invalidates calendar query keys (`schedule`, `appointments`,
 *      `scheduler-*`) so any server-side time formatting is refetched
 *      under the new zone. The grid re-renders in place; no full reload.
 *
 * Use `value: 0` to map back to "Account Default" (the API will set
 * `users_timezones.active='0'` so the resolution chain in
 * `AccountTimezone::effectiveIanaTimezone` falls through to
 * `aa_accounts.timezone`).
 */
export function useSetUserTimezone() {
  const queryClient = useQueryClient();
  const updateUser = useAuthStore((s) => s.updateUser);

  return useMutation({
    mutationFn: async (timezoneID: number) => {
      const response = await apiClient.put<ApiResponse<SetTimezoneResponse>>(
        '/user/timezone',
        { timezoneID },
      );
      return response.data?.data;
    },
    onSuccess: (data) => {
      if (!data) return;
      updateUser({
        timezone: data.timezone,
        timezoneID: data.timezoneID,
        ...(data.tzRules ? { tzRules: data.tzRules } : {}),
      });
      // Refresh anything time-zone-aware that the server formats. The
      // calendar grid, scheduler payload, and overview tab all live
      // under these prefixes.
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['scheduler-preference'] });
    },
  });
}
